import React, { useEffect, useMemo, useState, useContext } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import api from "../../api";
import { isComplaintTask, assigneeOptionsFor, assigneeOptionLabel, assignmentErrorMessage } from "../../utils/complaintAssignee";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Pagination,
  Spinner,
  Badge,
} from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";
import { taskDisplayTitle } from "../../utils/taskDisplay";
import "./AdminAssingtask.css";

// Simplified backdated Task Date -- local-date "YYYY-MM-DD", matching a
// native <input type="date">'s own value format. Never uses toISOString()
// (UTC-based -- can roll the calendar date back a day in a timezone east
// of UTC); built from local Date getters instead so "today" always means
// the browser's own local today.
const todayDateString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const initialTask = {
  title: "",
  description: "",
  status: "Pending",
  category_id: "",
  subcategory_id: "",
  // Simplified backdated Task Date -- kept "" in this static constant
  // (never baked in here, which would go stale across midnight); always
  // overridden with a fresh todayDateString() in openCreateModal() below,
  // every time the modal actually opens.
  task_date: "",
};

export default function AdminAssingtask() {
  const { user } = useContext(AuthContext);

  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");

  const [tasks, setTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTaskRow, setAssignTaskRow] = useState(null);
  const [assignUserId, setAssignUserId] = useState("");
  const [saving, setSaving] = useState(false);

  const [modalUnitId, setModalUnitId] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);

  const [createUnitId, setCreateUnitId] = useState("");
  const [createUserId, setCreateUserId] = useState("");
  const [createUserSearch, setCreateUserSearch] = useState("");

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [subcatLoading, setSubcatLoading] = useState(false);
  const [newTask, setNewTask] = useState(initialTask);

  useEffect(() => {
    loadUnits();
    loadTasks();
    loadAllUsers();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, unitId, perPage]);

  useEffect(() => {
    if (!showCreateModal) return;

    setNewTask((prev) => ({
      ...prev,
      category_id: "",
      subcategory_id: "",
    }));

    setSubcategories([]);
    loadCategories(createUnitId || "");
  }, [showCreateModal, createUnitId]);

  useEffect(() => {
    if (!showCreateModal) return;
    loadSubcategories(newTask.category_id, createUnitId || "");
  }, [showCreateModal, newTask.category_id, createUnitId]);

  const loadUnits = async () => {
    try {
      const { data } = await api.get(`/units`);
      setUnits(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load units", error);
    }
  };

  const loadAllUsers = async () => {
    try {
      const { data } = await api.get(`/users`);
      setAllUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load users", error);
    }
  };

  const loadTasks = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/tasks`);
      setTasks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load tasks", error);
      alert("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const uniqById = (arr = []) => {
    return Array.from(new Map(arr.map((x) => [String(x.id), x])).values());
  };

  const sortByName = (arr = []) => {
    return [...arr].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  };

  const filterByUnit = (rows = [], selectedUnitId) => {
    if (!selectedUnitId) return rows;

    return rows.filter(
      (row) =>
        String(row.unit_id) === String(selectedUnitId) ||
        (Array.isArray(row.units) &&
          row.units.some((u) => String(u) === String(selectedUnitId)))
    );
  };

  const filterByUnitIfPresent = (rows = [], selectedUnitId) => {
    if (!selectedUnitId) return rows;

    return rows.filter((row) => {
      const hasUnitField =
        Object.prototype.hasOwnProperty.call(row, "unit_id") ||
        Object.prototype.hasOwnProperty.call(row, "units");

      if (!hasUnitField) return true;

      return (
        String(row.unit_id) === String(selectedUnitId) ||
        (Array.isArray(row.units) &&
          row.units.some((u) => String(u) === String(selectedUnitId)))
      );
    });
  };

  const loadCategories = async (unitIdForCreate) => {
    try {
      setCatLoading(true);

      const { data } = await api.get(`/categories`, {
        params: unitIdForCreate ? { unit_id: unitIdForCreate } : undefined,
      });

      const deduped = uniqById(data || []);
      const filteredRows = filterByUnit(deduped, unitIdForCreate);

      setCategories(sortByName(filteredRows));
    } catch (error) {
      console.error("Failed to load categories", error);
      setCategories([]);
    } finally {
      setCatLoading(false);
    }
  };

  const loadSubcategories = async (categoryId, unitIdForCreate) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    try {
      setSubcatLoading(true);

      const { data } = await api.get(`/subcategories`, {
        params: { category_id: Number(categoryId) },
      });

      let rows = uniqById(data || []);
      rows = rows.filter((sc) => String(sc.category_id) === String(categoryId));
      rows = filterByUnitIfPresent(rows, unitIdForCreate);

      setSubcategories(sortByName(rows));
    } catch (error) {
      console.error("Failed to load subcategories", error);
      setSubcategories([]);
    } finally {
      setSubcatLoading(false);
    }
  };

  const resolveUserName = (id, fallback) => {
    return (
      allUsers.find((u) => String(u.id) === String(id))?.name ||
      fallback ||
      (id ? `#${id}` : "Not assigned")
    );
  };

  const initials = (name = "") => {
    return (
      name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() || "")
        .join("") || "U"
    );
  };

  const getStatusBadge = (status, assignedTo) => {
    if (!assignedTo) {
      return (
        <Badge className="unit-status assigntask-status-unassigned">
          Unassigned
        </Badge>
      );
    }

    const statusSlug = (status || "Pending").toLowerCase().replace(/\s+/g, "-");

    return (
      <Badge className={`unit-status assigntask-status-${statusSlug}`}>
        {status || "Pending"}
      </Badge>
    );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return (tasks || []).filter((task) => {
      const passSearch =
        !q ||
        [
          task.title,
          task.description,
          task.category_name,
          task.status,
          task.assigned_user_name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));

      const passStatus =
        statusFilter === "ALL"
          ? true
          : statusFilter === "Unassigned"
          ? !task.assigned_to
          : (task.status || "").toLowerCase() === statusFilter.toLowerCase();

      const passUnit = unitId
        ? String(task.unit_id || "") === String(unitId)
        : true;

      return passSearch && passStatus && passUnit;
    });
  }, [tasks, search, statusFilter, unitId]);

  const stats = useMemo(() => {
    const total = tasks.length;
    const unassigned = tasks.filter((t) => !t.assigned_to).length;
    const pending = tasks.filter(
      (t) => (t.status || "").toLowerCase() === "pending"
    ).length;
    const inprogress = tasks.filter(
      (t) => (t.status || "").toLowerCase() === "in progress"
    ).length;
    const completed = tasks.filter(
      (t) => (t.status || "").toLowerCase() === "completed"
    ).length;

    return {
      total,
      unassigned,
      pending,
      inprogress,
      completed,
    };
  }, [tasks]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  const pageData = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page, perPage]);

  const modalUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();

    const filteredUsers = (allUsers || []).filter((u) => {
      const inUnit = !modalUnitId || String(u.unit_id) === String(modalUnitId);

      const passSearch =
        !q ||
        [u.name, u.email]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));

      return inUnit && passSearch;
    });
    // IT Support complaint: Technicians only, plus the current owner kept
    // as-is (utils/complaintAssignee.js -- same rule the backend enforces).
    return assigneeOptionsFor(assignTaskRow, filteredUsers, { allUsers });
  }, [allUsers, modalUnitId, userSearch, assignTaskRow]);
  const assignIsComplaint = isComplaintTask(assignTaskRow);

  const createModalUsers = useMemo(() => {
    const q = createUserSearch.trim().toLowerCase();

    return (allUsers || []).filter((u) => {
      const inUnit = !createUnitId || String(u.unit_id) === String(createUnitId);

      const passSearch =
        !q ||
        [u.name, u.email]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));

      return inUnit && passSearch;
    });
  }, [allUsers, createUnitId, createUserSearch]);

  const openAssignModal = (row) => {
    setAssignTaskRow(row);
    setAssignUserId(row.assigned_to ? String(row.assigned_to) : "");

    const assignee = allUsers.find(
      (u) => String(u.id) === String(row.assigned_to)
    );

    setModalUnitId(assignee?.unit_id ? String(assignee.unit_id) : unitId || "");
    setUserSearch("");
    setShowAssignModal(true);
  };

  const assignNow = async () => {
    if (!assignTaskRow?.id || !assignUserId) {
      alert("Please select a user to assign.");
      return;
    }

    try {
      setSaving(true);

      await api.put(`/tasks/assign/${assignTaskRow.id}`, {
        assigned_to: Number(assignUserId),
      });

      setShowAssignModal(false);
      setAssignTaskRow(null);
      await loadTasks();

      alert("Task assignment saved.");
    } catch (error) {
      console.error("Assign Task Error:", error);
      alert(assignmentErrorMessage(error, "Failed to assign task."));
    } finally {
      setSaving(false);
    }
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setNewTask({ ...initialTask, task_date: todayDateString() });
    setCreateUnitId(unitId || "");
    setCreateUserId("");
    setCreateUserSearch("");
    setSubcategories([]);
    loadCategories(unitId || "");
  };

  // Estimated TAT -- read-only display of the Subcategory's own configured
  // tat_value/tat_unit (services/subcategoryTatService.js is the single
  // source of truth). Never editable here and never sent in the payload --
  // the backend independently resolves TAT from sub_category_id alone.
  const selectedSubcategory = subcategories.find(
    (s) => String(s.id) === String(newTask.subcategory_id)
  );
  const subcategoryHasTat = !!(
    selectedSubcategory &&
    selectedSubcategory.tat_value != null &&
    selectedSubcategory.tat_unit
  );

  const createTask = async () => {
    if (!newTask.title?.trim()) {
      alert("Title is required.");
      return;
    }

    if (!newTask.category_id) {
      alert("Please select a category.");
      return;
    }

    if (!createUnitId) {
      alert("Please select a Unit.");
      return;
    }

    if (!createUserId) {
      alert("Please choose an assignee.");
      return;
    }

    if (!newTask.task_date) {
      alert("Task Date is required.");
      return;
    }

    try {
      setCreating(true);

      const payload = {
        title: newTask.title.trim(),
        description: (newTask.description || "").trim(),
        status: (newTask.status || "pending").toLowerCase(),
        category_id: Number(newTask.category_id),
        sub_category_id: newTask.subcategory_id
          ? Number(newTask.subcategory_id)
          : null,
        unit_id: Number(createUnitId),
        created_by: user?.id,
        assigned_to: Number(createUserId),
        // Simplified backdated Task Date -- backend independently
        // re-validates (never trusts this alone), rejecting a future date
        // regardless of what a crafted request sends.
        task_date: newTask.task_date,
        // No tat_value/tat_unit is ever sent -- TAT is resolved purely
        // server-side from category_id/sub_category_id.
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      await api.post(`/admin-assign`, payload);

      setShowCreateModal(false);
      await loadTasks();

      alert("Task created & assigned.");
    } catch (error) {
      console.error("Create Task Error:", error);
      alert(error?.response?.data?.error || error?.response?.data?.message || "Failed to create task.");
    } finally {
      setCreating(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setUnitId("");
    setPage(1);
  };

  const exportCSV = () => {
    if (!filtered.length) {
      alert("No data available to export");
      return;
    }

    const headers = [
      "ID",
      "Title",
      "Description",
      "Category",
      "Assigned To",
      "Status",
    ];

    const rows = filtered.map((task) => [
      task.id,
      taskDisplayTitle(task),
      task.description,
      task.category_name,
      resolveUserName(task.assigned_to, task.assigned_user_name),
      task.assigned_to ? task.status : "Unassigned",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value || "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "assigned-tasks.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="unit-page assigntask-theme">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Task Management</div>
          <h2>Assign / Reassign Tasks</h2>
          <p>Create tasks, assign users, reassign work and track task status.</p>
        </div>

        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={loadTasks}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>

          <Button className="unit-btn unit-btn-light" onClick={exportCSV}>
            <i className="fa-solid fa-file-csv" aria-hidden="true"></i>
            Export CSV
          </Button>

          <Button className="unit-btn unit-btn-primary" onClick={openCreateModal}>
            <i className="fa-solid fa-plus" aria-hidden="true"></i>
            New Task
          </Button>
        </div>
      </div>

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <div className="assigntask-stat-icon assigntask-stat-icon-primary">
            <i className="fa-solid fa-list-check" aria-hidden="true"></i>
          </div>
          <div className="assigntask-stat-text">
            <span>Total Tasks</span>
            <strong>{stats.total}</strong>
          </div>
        </div>

        <div className="unit-stat-card">
          <div className="assigntask-stat-icon assigntask-stat-icon-danger">
            <i className="fa-solid fa-user-slash" aria-hidden="true"></i>
          </div>
          <div className="assigntask-stat-text">
            <span>Unassigned</span>
            <strong>{stats.unassigned}</strong>
          </div>
        </div>

        <div className="unit-stat-card">
          <div className="assigntask-stat-icon assigntask-stat-icon-warning">
            <i className="fa-solid fa-hourglass-half" aria-hidden="true"></i>
          </div>
          <div className="assigntask-stat-text">
            <span>Pending</span>
            <strong>{stats.pending}</strong>
          </div>
        </div>

        <div className="unit-stat-card">
          <div className="assigntask-stat-icon assigntask-stat-icon-accent">
            <i className="fa-solid fa-arrows-rotate" aria-hidden="true"></i>
          </div>
          <div className="assigntask-stat-text">
            <span>In Progress</span>
            <strong>{stats.inprogress}</strong>
          </div>
        </div>

        <div className="unit-stat-card">
          <div className="assigntask-stat-icon assigntask-stat-icon-success">
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
          </div>
          <div className="assigntask-stat-text">
            <span>Completed</span>
            <strong>{stats.completed}</strong>
          </div>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-clipboard-user assigntask-list-icon" aria-hidden="true"></i>
              Task Assignment List
            </h5>
            <p>Search, filter, assign and reassign task records.</p>
          </div>

          <div className="unit-toolbar-actions">
            <InputGroup className="unit-search">
              <InputGroup.Text aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </InputGroup.Text>

              <Form.Control
                placeholder="Search title, description, category..."
                aria-label="Search title, description, category"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <Button className="unit-clear-btn" onClick={() => setSearch("")}>
                  Clear
                </Button>
              )}
            </InputGroup>

            <Form.Select
              className="unit-page-size"
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
            >
              <option value="">All Units</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Status</option>
              <option value="Unassigned">Unassigned</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </Form.Select>

            <Form.Select
              className="unit-page-size"
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
            >
              <option value={5}>5 rows</option>
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={50}>50 rows</option>
            </Form.Select>

            {(search || unitId || statusFilter !== "ALL") && (
              <Button className="unit-btn unit-btn-light" onClick={clearFilters}>
                Reset
              </Button>
            )}
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader">
              <Spinner animation="border" />
              <p>Loading tasks...</p>
            </div>
          ) : (
            <table className="table unit-table">
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>ID</th>
                  <th>Task</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                  <th className="text-end" style={{ width: "150px" }}>
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {pageData.length > 0 ? (
                  pageData.map((task) => {
                    const assignedUser = resolveUserName(
                      task.assigned_to,
                      task.assigned_user_name
                    );

                    return (
                      <tr key={task.id}>
                        <td>
                          <span className="unit-id">#{task.id}</span>
                        </td>

                        <td>
                          <div className="unit-name-box">
                            <div className="unit-letter">
                              {taskDisplayTitle(task).charAt(0).toUpperCase() || "T"}
                            </div>

                            <div>
                              <strong>{taskDisplayTitle(task)}</strong>
                              <small>Task ID: {task.id}</small>
                            </div>
                          </div>
                        </td>

                        <td
                          style={{
                            maxWidth: "320px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={task.description || ""}
                        >
                          {task.description || "-"}
                        </td>

                        <td>{task.category_name || "-"}</td>

                        <td>
                          {assignedUser && assignedUser !== "Not assigned" ? (
                            <div className="unit-name-box">
                              <div className="unit-letter">
                                {initials(assignedUser)}
                              </div>
                              <div>
                                <strong>{assignedUser}</strong>
                                <small>Assignee</small>
                              </div>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>{getStatusBadge(task.status, task.assigned_to)}</td>

                        <td>
                          <div className="unit-actions">
                            <Button
                              className="unit-btn unit-btn-primary"
                              onClick={() => openAssignModal(task)}
                              aria-label={`${task.assigned_to ? "Reassign" : "Assign"} ${
                                taskDisplayTitle(task) || "task"
                              }`}
                            >
                              <i
                                className={`fa-solid ${
                                  task.assigned_to ? "fa-arrows-rotate" : "fa-user-plus"
                                }`}
                                aria-hidden="true"
                              ></i>
                              {task.assigned_to ? "Reassign" : "Assign"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-clipboard-list" aria-hidden="true"></i>
                        </div>
                        <h5>No tasks found</h5>
                        <p>Try another filter or create a new task.</p>

                        <Button
                          className="unit-btn unit-btn-primary"
                          onClick={openCreateModal}
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true"></i>
                          New Task
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing{" "}
            <strong>{filtered.length === 0 ? 0 : (page - 1) * perPage + 1}</strong>{" "}
            to <strong>{Math.min(page * perPage, filtered.length)}</strong> of{" "}
            <strong>{filtered.length}</strong> entries
          </div>

          {totalPages > 1 && (
            <Pagination className="unit-pagination">
              <Pagination.Prev
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              />

              {compactPageItems([...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === page}
                  onClick={() => setPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              )), page)}

              <Pagination.Next
                disabled={page >= totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(totalPages, prev + 1))
                }
              />
            </Pagination>
          )}
        </div>
      </div>

      <Modal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        centered
        className="unit-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {assignTaskRow?.assigned_to ? "Reassign Task" : "Assign Task"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="unit-card mb-3" style={{ boxShadow: "none" }}>
            <div className="p-3">
              <h6 className="mb-1">{taskDisplayTitle(assignTaskRow) || "-"}</h6>
              <p className="mb-2 text-muted">{assignTaskRow?.description || "-"}</p>
              {getStatusBadge(assignTaskRow?.status, assignTaskRow?.assigned_to)}
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Unit</Form.Label>
            <Form.Select
              value={modalUnitId}
              onChange={(e) => setModalUnitId(e.target.value)}
            >
              <option value="">All Units</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Search User</Form.Label>
            <InputGroup>
              <InputGroup.Text aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </InputGroup.Text>
              <Form.Control
                placeholder="Type name or email..."
                aria-label="Search user by name or email"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />

              {userSearch && (
                <Button className="unit-clear-btn" onClick={() => setUserSearch("")}>
                  Clear
                </Button>
              )}
            </InputGroup>

            <small className="text-muted">
              {modalUsers.length} user{modalUsers.length === 1 ? "" : "s"} found
            </small>
          </Form.Group>

          <Form.Group>
            <Form.Label htmlFor="assign-task-user">
              {assignIsComplaint
                ? "Assign Technician"
                : assignTaskRow?.assigned_to ? "Reassign To" : "Assign To"}
            </Form.Label>

            <Form.Select
              id="assign-task-user"
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
            >
              <option value="">{assignIsComplaint ? "-- Select Technician --" : "-- Select User --"}</option>
              {modalUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {assigneeOptionLabel(u)}
                  {u.email ? ` • ${u.email}` : ""}
                  {u.unit_id ? ` • Unit #${u.unit_id}` : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => setShowAssignModal(false)}
          >
            Cancel
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={assignNow}
            disabled={!assignUserId || saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        centered
        className="unit-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Task</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              placeholder="Short task title"
              value={newTask.title}
              onChange={(e) =>
                setNewTask({ ...newTask, title: e.target.value })
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Details, acceptance criteria, etc."
              value={newTask.description}
              onChange={(e) =>
                setNewTask({ ...newTask, description: e.target.value })
              }
            />
          </Form.Group>

          <div className="row g-3">
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Unit</Form.Label>
                <Form.Select
                  value={createUnitId}
                  onChange={(e) => setCreateUnitId(e.target.value)}
                >
                  <option value="">Select Unit</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={newTask.category_id}
                  onChange={(e) =>
                    setNewTask({
                      ...newTask,
                      category_id: e.target.value,
                      subcategory_id: "",
                    })
                  }
                >
                  <option value="">
                    {catLoading ? "Loading..." : "-- Select Category --"}
                  </option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Subcategory</Form.Label>
                <Form.Select
                  value={newTask.subcategory_id}
                  disabled={!newTask.category_id || subcatLoading}
                  onChange={(e) =>
                    setNewTask({ ...newTask, subcategory_id: e.target.value })
                  }
                >
                  <option value="">
                    {!newTask.category_id
                      ? "Select category first"
                      : subcatLoading
                      ? "Loading..."
                      : "-- Select Subcategory --"}
                  </option>

                  {subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={newTask.status}
              onChange={(e) =>
                setNewTask({ ...newTask, status: e.target.value })
              }
            >
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </Form.Select>
          </Form.Group>

          <hr />

          <h6 className="mb-3">Assign User</h6>

          <Form.Group className="mb-3">
            <Form.Label>Search User</Form.Label>
            <InputGroup>
              <InputGroup.Text aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </InputGroup.Text>
              <Form.Control
                placeholder="Type name or email..."
                aria-label="Search user by name or email"
                value={createUserSearch}
                onChange={(e) => setCreateUserSearch(e.target.value)}
              />

              {createUserSearch && (
                <Button
                  className="unit-clear-btn"
                  onClick={() => setCreateUserSearch("")}
                >
                  Clear
                </Button>
              )}
            </InputGroup>

            <small className="text-muted">
              {createModalUsers.length} user
              {createModalUsers.length === 1 ? "" : "s"} found
            </small>
          </Form.Group>

          <Form.Group>
            <Form.Label>Assign To</Form.Label>
            <Form.Select
              value={createUserId}
              onChange={(e) => setCreateUserId(e.target.value)}
            >
              <option value="">-- Select User --</option>
              {createModalUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                  {u.email ? ` • ${u.email}` : ""}
                  {u.unit_id ? ` • Unit #${u.unit_id}` : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {newTask.subcategory_id && (
            <Form.Group className="mt-3">
              <Form.Label>Estimated TAT</Form.Label>
              {subcategoryHasTat ? (
                <Form.Control
                  readOnly
                  value={`${selectedSubcategory.tat_value} ${selectedSubcategory.tat_unit}`}
                />
              ) : (
                <Form.Text className="text-muted d-block">
                  No Turnaround Time is configured for this subcategory yet — it will be created without a due date.
                </Form.Text>
              )}
            </Form.Group>
          )}

          {/* Simplified backdated Task Date -- available to every
              user/role that can already open this Assign Task modal at
              all; no permission, no feature flag. `max` blocks a future
              pick in the UI as a courtesy only -- the backend
              independently re-validates and rejects a future date
              regardless of what a crafted request sends. */}
          <Form.Group className="mt-3">
            <Form.Label>
              Task Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              max={todayDateString()}
              value={newTask.task_date}
              onChange={(e) =>
                setNewTask({ ...newTask, task_date: e.target.value })
              }
              required
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => setShowCreateModal(false)}
          >
            Cancel
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={createTask}
            disabled={
              creating ||
              !newTask.title.trim() ||
              !newTask.category_id ||
              !newTask.task_date
            }
          >
            {creating ? "Creating..." : "Create Task"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}